<template>
  <div class="reporting-section" v-if="storyPosts.length">
    <div class="section-header">
      <i class="material-icons section-icon">web_stories</i>
      <h2>{{ $t('dash.reporting.section.story-list.title') }}</h2>
      <div class="filter-wrap" v-click-outside="closeFilter">
        <button
          class="filter-toggle-btn"
          :class="{ 'filter-toggle-btn--active': hasActiveFilters }"
          @click="showFilter = !showFilter"
        >
          <i class="material-icons">filter_list</i>
          <span v-if="activeFilterCount" class="filter-badge">{{ activeFilterCount }}</span>
        </button>
        <ReportingFilter
          v-if="showFilter"
          :filter="filter"
          :creators="creatorsAvailable"
          :labels="labelsAvailable"
          :hide-types="true"
          @update:filter="filter = $event"
        />
      </div>
    </div>

    <div class="post-table">
      <div class="post-table__head">
        <span class="col-header sortable" :class="sortClass('date')" @click="onSort('date')">
          {{ cap($t('dash.reporting.pdf.listItem.date')) }}<i class="material-icons sort-icon">{{ sortIcon('date') }}</i>
        </span>
        <span class="col-header">{{ cap($t('dash.reporting.pdf.listItem.publication')) }}</span>
        <span class="col-header sortable align-right" :class="sortClass('impressions')" @click="onSort('impressions')">
          {{ cap($t('dash.reporting.impressions')) }}<i class="material-icons sort-icon">{{ sortIcon('impressions') }}</i>
        </span>
        <Tooltip :text="$t('dash.reporting.tooltip.stories_replies')" position="bottom">
          <span class="col-header sortable align-right" :class="sortClass('replies')" @click="onSort('replies')">
            {{ cap($t('dash.reporting.replies')) }}<i class="material-icons sort-icon">{{ sortIcon('replies') }}</i>
          </span>
        </Tooltip>
        <span class="col-header sortable align-right" :class="sortClass('shares')" @click="onSort('shares')">
          {{ cap($t('dash.reporting.share')) }}<i class="material-icons sort-icon">{{ sortIcon('shares') }}</i>
        </span>
        <span class="col-header sortable align-right" :class="sortClass('interactions')" @click="onSort('interactions')">
          {{ cap($t('dash.reporting.total_interactions')) }}<i class="material-icons sort-icon">{{ sortIcon('interactions') }}</i>
        </span>
        <Tooltip :text="$t('dash.reporting.tooltip.stories_profile_visits')" position="bottom">
          <span class="col-header sortable align-right" :class="sortClass('profile_visits')" @click="onSort('profile_visits')">
            {{ cap($t('dash.reporting.profile_visits')) }}<i class="material-icons sort-icon">{{ sortIcon('profile_visits') }}</i>
          </span>
        </Tooltip>
        <span class="col-header sortable align-right" :class="sortClass('follows')" @click="onSort('follows')">
          {{ cap($t('dash.reporting.follows')) }}<i class="material-icons sort-icon">{{ sortIcon('follows') }}</i>
        </span>
      </div>
      <div class="post-table__row" v-for="post in paginatedPosts" :key="post.id || post.post_id">
        <span class="cell-date">{{ fmtDate(post.created_time || post.timestamp) }}</span>
        <div class="cell-post">
          <div class="post-thumb story-thumb">
            <img v-if="post.attachment_url" :src="post.attachment_url" alt="">
            <i v-else class="material-icons">crop_portrait</i>
          </div>
          <p class="post-msg">{{ post.message || post.caption || '—' }}</p>
        </div>
        <span class="cell-num">{{ fmt(post.post_impressions) }}</span>
        <span class="cell-num">{{ fmt(post.replies) }}</span>
        <span class="cell-num">{{ fmt(post.shares) }}</span>
        <span class="cell-num">{{ fmt(post.total_interactions) }}</span>
        <span class="cell-num">{{ fmt(post.profile_visits) }}</span>
        <span class="cell-num">{{ fmt(post.follows) }}</span>
      </div>
    </div>

    <div class="pagination" v-if="pageCount > 1">
      <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="material-icons">chevron_left</i>
      </button>
      <span class="page-info">{{ currentPage }} / {{ pageCount }}</span>
      <button class="page-btn" :disabled="currentPage === pageCount" @click="currentPage++">
        <i class="material-icons">chevron_right</i>
      </button>
    </div>
  </div>
</template>

<script>
import Tooltip from '../tooltip/tooltipInit.vue'
import ReportingFilter from './ReportingFilter.vue'
import vClickOutside from 'vue-click-outside'

const PAGE_SIZE = 20

export default {
  name: 'ReportingStories',
  components: { Tooltip, ReportingFilter },
  directives: { clickOutside: vClickOutside },
  props: {
    stats: { type: Object, required: true },
    page: { type: Object, default: null },
    solutionPostMap: { type: Object, default: function() { return {} } },
  },
  data() {
    return {
      sortKey: 'date',
      sortDir: 'desc',
      currentPage: 1,
      showFilter: false,
      filter: {
        solutionOnly: false,
        creators: {},
        labels: [],
      },
    }
  },
  mounted() {
    // Always refresh (not just when empty) — otherwise labels created/edited
    // since the store was first populated this session never show up here.
    this.$store.dispatch('labels/init')
  },
  computed: {
    storyPosts() {
      var posts = this.stats.posts || (this.stats.page && this.stats.page.posts) || []
      return posts.filter(function(p) { return p.type === 'Story' })
    },
    creatorsAvailable() {
      var vm = this
      var seen = {}
      this.storyPosts.forEach(function(p) {
        var id = String(p.post_id || p.id || '')
        if (!id) return
        var entry = vm.solutionPostMap[id]
        if (entry && entry.creator && entry.creator.id && !seen[entry.creator.id]) {
          seen[entry.creator.id] = entry.creator
        }
      })
      return Object.values(seen)
    },
    labelsAvailable() {
      return this.$store.getters['labels/getLabelsList']
    },
    hasActiveFilters() {
      if (this.filter.solutionOnly) return true
      if (Object.values(this.filter.creators || {}).some(function(v) { return v === false })) return true
      if (this.filter.labels && this.filter.labels.length) return true
      return false
    },
    activeFilterCount() {
      var count = 0
      if (this.filter.solutionOnly) count++
      count += Object.values(this.filter.creators || {}).filter(function(v) { return v === false }).length
      count += (this.filter.labels && this.filter.labels.length) ? 1 : 0
      return count
    },
    sortedPosts() {
      var list = this.applyPostFilter(this.storyPosts)
      var dir = this.sortDir === 'asc' ? 1 : -1
      var key = this.sortKey
      list.sort(function(a, b) {
        var va, vb
        if (key === 'date')          { va = new Date(a.created_time || a.timestamp); vb = new Date(b.created_time || b.timestamp) }
        else if (key === 'impressions')   { va = a.post_impressions || 0; vb = b.post_impressions || 0 }
        else if (key === 'replies')       { va = a.replies || 0; vb = b.replies || 0 }
        else if (key === 'shares')        { va = a.shares || 0; vb = b.shares || 0 }
        else if (key === 'interactions')  { va = a.total_interactions || 0; vb = b.total_interactions || 0 }
        else if (key === 'profile_visits'){ va = a.profile_visits || 0; vb = b.profile_visits || 0 }
        else if (key === 'follows')       { va = a.follows || 0; vb = b.follows || 0 }
        else                              { va = new Date(a.created_time || a.timestamp); vb = new Date(b.created_time || b.timestamp) }
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
      return list
    },
    pageCount() {
      return Math.ceil(this.sortedPosts.length / PAGE_SIZE)
    },
    paginatedPosts() {
      var start = (this.currentPage - 1) * PAGE_SIZE
      return this.sortedPosts.slice(start, start + PAGE_SIZE)
    },
  },
  watch: {
    filter: {
      deep: true,
      handler() { this.currentPage = 1 },
    },
  },
  methods: {
    closeFilter() {
      this.showFilter = false
    },
    applyPostFilter(posts) {
      var vm = this
      var rf = this.filter
      var result = posts.slice()
      if (rf.solutionOnly) {
        result = result.filter(function(p) {
          var id = String(p.post_id || p.id || '')
          if (!id) return false
          var entry = vm.solutionPostMap[id]
          if (!entry) return true
          return entry.is_solution_post
        })
      }
      if (rf.creators && Object.values(rf.creators).some(function(v) { return v === false })) {
        result = result.filter(function(p) {
          var id = String(p.post_id || p.id || '')
          if (!id) return true
          var entry = vm.solutionPostMap[id]
          var creatorId = entry && entry.creator && entry.creator.id
          if (!creatorId) return true
          return rf.creators[creatorId] !== false
        })
      }
      if (rf.labels && rf.labels.length) {
        result = result.filter(function(p) {
          var id = String(p.post_id || p.id || '')
          if (!id) return false
          var entry = vm.solutionPostMap[id]
          var postLabels = entry && entry.labels
          if (!postLabels || !postLabels.length) return false
          return rf.labels.some(function(labelId) { return postLabels.includes(labelId) })
        })
      }
      return result
    },
    onSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
      this.currentPage = 1
    },
    sortClass(key) {
      return { 'col-active': this.sortKey === key }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return 'unfold_more'
      return this.sortDir === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    cap(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    fmt(n) {
      if (n == null) return '—'
      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k'
      return n.toString()
    },
    fmtDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; flex: 1; }

.filter-wrap { position: relative; flex-shrink: 0; }
.filter-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1.5px solid #e0d0ea !important;
  background: #fff !important;
  color: #6B1F4D !important;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
}
.filter-toggle-btn:hover,
.filter-toggle-btn--active { background: #f1edf5 !important; border-color: #6B1F4D !important; }
.filter-toggle-btn .material-icons { font-size: 16px; }
.filter-badge {
  background: #6B1F4D;
  color: #fff;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  min-width: 16px;
  text-align: center;
  line-height: 1.4;
}

.post-table { width: 100%; overflow-x: auto; }
.post-table__head,
.post-table__row {
  display: grid;
  grid-template-columns: 155px 1fr 110px 90px 85px 155px 150px 95px;
  gap: 8px;
  align-items: center;
  padding: 8px 4px;
}
.post-table__head {
  border-bottom: 1px solid #f1edf5;
  padding-bottom: 10px;
}
.post-table__row {
  border-bottom: 1px solid #f9f7fb;
  font-size: 13px;
  color: #121826;
}
.post-table__row:last-child { border-bottom: none; }
.align-right { text-align: right; justify-content: flex-end; }

.col-header {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
}
.col-header.sortable { cursor: pointer; user-select: none; }
.col-header.sortable:hover { color: #6B1F4D; }
.col-header.col-active { color: #6B1F4D; }
.sort-icon { font-size: 14px; opacity: 0.6; }
.col-active .sort-icon { opacity: 1; }

.cell-date { color: #9ba3b7; font-size: 12px; white-space: nowrap; min-width: 0; }
.cell-post { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
.post-thumb {
  width: 42px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 5.6px;
  overflow: hidden;
  background: #f1edf5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0a8d0;
}
.story-thumb { height: 56px; }
.post-thumb img { width: 100%; height: 100%; object-fit: cover; }
.post-thumb .material-icons { font-size: 18px; }
.post-msg {
  font-size: 12px;
  color: #555E75;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
}
.cell-num { text-align: right; font-size: 13px; font-weight: 500; min-width: 0; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1edf5;
}
.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e0d0ea;
  background: #fff;
  color: #6B1F4D;
  cursor: pointer;
  transition: all 0.15s;
}
.page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.page-btn:not(:disabled):hover { background: #f1edf5; }
.page-btn .material-icons { font-size: 18px; }
.page-info { font-size: 13px; color: #555E75; font-weight: 500; min-width: 60px; text-align: center; }
</style>
